// Transport core: the single place that talks HTTP to the Yoreflix API.
//
// One credentialed fetch, one envelope to unwrap, typed errors out. The four
// HttpOnly cookies (yoreflix_session / _rt / _ct / _ik) ride along automatically
// via `credentials: 'include'`; JS never sees them. The only token JS shuttles is
// the CSRF: sent on guarded calls via the X-Yoreflix-CSRF header, captured back
// from each response (header when exposed, else the body's Data.CSRFToken).

import { apiUrl } from './config'
import { ApiError, ForbiddenError, StaleTokenError, UnauthorizedError } from './errors'
import type { ApiEnvelope } from './types'

export const CSRF_HEADER = 'X-Yoreflix-CSRF'

export interface RequestOptions {
  method?: 'GET' | 'POST'
  /** JSON request body (sets Content-Type). Omit for a bodyless GET/POST. */
  body?: Record<string, unknown>
  /** Extra request headers (e.g. the CSRF header on guarded calls). */
  headers?: Record<string, string>
  /**
   * Called with the raw response and parsed envelope on EVERY outcome, so the
   * client can capture the rotated CSRF token (from the header or the body)
   * regardless of success.
   */
  onResponse?: (res: Response, envelope: ApiEnvelope<unknown>) => void
}

/**
 * Send one API request and return its unwrapped Data, or throw a typed error.
 *
 * @param path Yoreflix/v1 path, e.g. 'auth/session'.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...opts.headers }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(apiUrl(path), {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })

  let envelope: ApiEnvelope<T>
  try {
    envelope = (await res.json()) as ApiEnvelope<T>
  } catch {
    throw new ApiError({ Code: res.status, Description: `Non-JSON response (HTTP ${res.status})` })
  }

  opts.onResponse?.(res, envelope as ApiEnvelope<unknown>)

  if (!envelope.Success || envelope.Error) {
    const err = envelope.Error ?? { Code: res.status, Description: 'Unknown error' }
    switch (err.Code) {
      case 401:
        throw new UnauthorizedError(err)
      case 403:
        throw new ForbiddenError(err)
      case 409:
      case 428:
        throw new StaleTokenError(err)
      default:
        throw new ApiError(err)
    }
  }

  return envelope.Data as T
}
